import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { UiAvatarComponent } from '../ui-avatar/ui-avatar.component';
import { EventsService } from '../../services/events.service';
import { UserContextService } from '../../services/user-context.service';
import { BusinessFuncsService } from '@/app/businessfuncs/shared/businessfuncs.service';
import { AvatarCacheService } from '../../services/avatar-cache.service';
import { canAccessForceCompleteWorkflow } from '../../services/force-complete-access';
import { UiButtonComponent } from '../ui-button/ui-button.component';

type NavDropdown = 'manager' | 'admin';

interface NavLink {
  label: string;
  icon: string;
  link: string;
  visible?: boolean;
}

const visibleOnly = (links: NavLink[]) => links.filter((l) => l.visible !== false);

const ADMIN_LINKS: NavLink[] = [
  { label: 'Бейджи', icon: 'fas fa-certificate', link: '/badges' },
  { label: 'Бизнес-функции', icon: 'fas fa-toolbox', link: '/businessfuncs' },
  { label: 'Вопросы', icon: 'fas fa-question-circle', link: '/questions' },
  { label: 'Сценарий подбора БФ', icon: 'fas fa-route', link: '/bf-quiz' },
  { label: 'Поля идеи', icon: 'fas fa-th-list', link: '/idea-fields' },
  { label: 'Справочник шагов', icon: 'fas fa-th-list', link: '/step-directory' },
  { label: 'Решения ЭС', icon: 'fas fa-scroll', link: '/decision-types' },
  { label: 'Группы', icon: 'fas fa-users-cog', link: '/groups' },
  { label: 'Оргединицы', icon: 'fas fa-map', link: '/orgunits' },
  { label: 'Пользователи', icon: 'fas fa-user-friends', link: '/users' },
  { label: 'Рабочие процессы', icon: 'fas fa-microchip', link: '/workflows' },
  { label: 'Статусы идеи', icon: 'fas fa-flag', link: '/ideastatuses' },
  {
    label: 'Категории группировки статусов',
    icon: 'fas fa-layer-group',
    link: '/ideastatusgroupingcategories',
  },
  { label: 'Теги', icon: 'fas fa-tags', link: '/workflowtags' },
  { label: 'Типы задач реализации', icon: 'fas fa-tasks', link: '/realizationtasktypes' },
  { label: 'Типы событий', icon: 'fas fa-keyboard', link: '/activityeventtypes' },
  { label: 'Торговые сети', icon: 'fas fa-store', link: '/tradingnetworks' },
  {
    label: 'Общие торговые сети',
    icon: 'fas fa-store-alt',
    link: '/customtradingnetworks',
  },
  { label: 'Логи', icon: 'pi pi-file', link: '/logs' },
  { label: 'Настройки дедупликации', icon: 'pi pi-cog', link: '/deduplication-settings' },
];

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  imports: [
    RouterLink,
    RouterLinkActive,
    TooltipModule,
    UiAvatarComponent,
    UiButtonComponent,
  ],
  providers: [AvatarCacheService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeAll()',
  },
})
export class NavbarComponent {
  private readonly ctxRoot = viewChild<ElementRef<HTMLElement>>('ctxRoot');
  private readonly events = inject(EventsService);
  private readonly businessFuncSvc = inject(BusinessFuncsService);
  private readonly avatarCacheSvc = inject(AvatarCacheService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly userContextSvc = inject(UserContextService);
  private readonly router = inject(Router);

  // ---------- state ----------
  /** equal: () => false — сервис мутирует один и тот же объект, поэтому уведомляем при каждом set() */
  readonly user = signal(this.userContextSvc.currentUser, { equal: () => false });
  readonly mobileMenuOpen = signal(false);
  readonly openedDropdown = signal<NavDropdown | null>(null);
  private readonly reportAccessRequested = signal(false);
  readonly ctxOpen = signal(false);
  readonly ctxSeen = signal(this.readCtxSeen());

  readonly selectedBf = computed(() => this.user()?.SelectedBF);
  readonly orgUnit = computed(() => this.user()?.OrgUnit);

  // адаптер над не-сигнальным сервисом: пересчитывается при обновлении user()
  readonly isFullContext = computed(() => {
    this.user();
    return this.userContextSvc.isFullContext();
  });

  readonly canSwitchBf = computed(() => {
    this.user();
    return this.userContextSvc.canSwitchBusinessFunction();
  });

  readonly isAdmin = computed(() => !!this.user()?.GlobalAdmin);
  readonly isModerator = computed(() => !!this.user()?.Moderator);
  readonly isSecretary = computed(
    () => !!(this.user()?.MRECSecretary || this.user()?.COECSecretary)
  );
  readonly isManagement = computed(
    () => this.isAdmin() || this.isModerator() || this.isSecretary()
  );
  readonly canForceCompleteWorkflow = computed(() =>
    canAccessForceCompleteWorkflow(this.user())
  );
  readonly canCreateIdea = computed(
    () => this.isFullContext() && !!this.selectedBf()?.AvailableIdeaCreate
  );

  readonly logoSrc = computed(() =>
    this.selectedBf()?.Id !== 17 ? 'assets/img/logo.png' : 'assets/img/logo-m-p.png'
  );

  readonly userPhotoUrl = computed(() => {
    const u = this.user();
    return this.avatarCacheSvc.getAvatarUrl(u?.Id ?? null, u?.Email ?? null) ?? undefined;
  });

  private readonly ideaReportAccess = rxResource({
    params: () => (this.reportAccessRequested() ? { userId: this.user().Id } : undefined),
    stream: ({ params }) => this.businessFuncSvc.getReportAccessBf(params.userId, true),
  });

  private readonly validatorReportAccess = rxResource({
    params: () => (this.reportAccessRequested() ? { userId: this.user().Id } : undefined),
    stream: ({ params }) => this.businessFuncSvc.getReportAccessBf(params.userId, false),
  });

  readonly hasIdeaReportAccess = computed(() => this.ideaReportAccess.value() === true);
  readonly hasValidatorReportAccess = computed(
    () => this.validatorReportAccess.value() === true
  );

  readonly mainLinks = computed<NavLink[]>(() => {
    const full = this.isFullContext();
    const bf = this.selectedBf();
    return visibleOnly([
      { label: 'Идеи', icon: 'fas fa-bolt', link: '/ideas-v2', visible: full },
      {
        label: 'Задачи',
        icon: 'fas fa-tasks',
        link: '/idea-expert-tasks',
        visible: full,
      },
      {
        label: 'Рейтинг',
        icon: 'fas fa-ranking-star',
        link: '/rating',
        visible: full && !!bf?.IsVisibleRating,
      },
    ]);
  });

  readonly managerLinks = computed<NavLink[]>(() => {
    const mgmt = this.isManagement();
    const ideaReports = this.hasIdeaReportAccess();
    return visibleOnly([
      {
        label: 'Список обращений',
        icon: 'fas fa-tasks',
        link: '/appeals',
        visible: !!this.selectedBf()?.AvailableIdeaCreate,
      },
      { label: 'Решения ЭС', icon: 'fas fa-scroll', link: '/decisions', visible: mgmt },
      {
        label: 'Управление группами',
        icon: 'fas fa-users-cog',
        link: '/bfgroups',
        visible: mgmt,
      },
      {
        label: 'Управление задачами',
        icon: 'fas fa-tasks',
        link: '/reassigmenttasks',
        visible: mgmt,
      },
      {
        label: 'Рассылка уведомлений в ЛК',
        icon: 'fas fa-envelope',
        link: '/idealknotifications',
        visible: mgmt,
      },
      {
        label: 'Принудительное завершение РП',
        icon: 'fas fa-flag-checkered',
        link: '/force-complete-workflow',
        visible: this.canForceCompleteWorkflow(),
      },
      {
        label: 'Список идей на обработку из чат-бота',
        icon: 'fas fa-tasks',
        link: '/tgideas',
        visible: mgmt,
      },
      {
        label: 'Список проблем запуска РП',
        icon: 'fas fa-tasks',
        link: '/workflow-instance-issues',
        visible: this.isAdmin() || this.isModerator(),
      },
      {
        label: 'История дедупликации',
        icon: 'pi pi-history',
        link: '/deduplication-history',
        visible: this.isAdmin() || this.isModerator(),
      },
      {
        label: 'Отчет по идеям',
        icon: 'fas fa-table',
        link: 'ideas-report',
        visible: ideaReports,
      },
      {
        label: 'Отчет по идеям расширенный (группировка)',
        icon: 'fas fa-table',
        link: 'expanded-ideas-with-grouping',
        visible: ideaReports,
      },
      {
        label: 'Отчет по идеям расширенный (статистика)',
        icon: 'fas fa-table',
        link: 'expanded-ideas-with-statistics',
        visible: ideaReports,
      },
      {
        label: 'Отчет по валидаторам',
        icon: 'fas fa-table',
        link: 'validators-report',
        visible: this.hasValidatorReportAccess(),
      },
      {
        label: 'Отчет о невыполненных задачах РП',
        icon: 'fas fa-table',
        link: 'wftasks-not-completed',
        visible: ideaReports,
      },
      {
        label: 'Отчет по средней продолжительности реализации идей',
        icon: 'fas fa-table',
        link: 'idea-implementation',
        visible: ideaReports,
      },
      {
        label: 'Отчет по задачам',
        icon: 'fas fa-table',
        link: 'task-report',
        visible: ideaReports,
      },
    ]);
  });

  readonly adminLinks = ADMIN_LINKS;

  constructor() {
    // обновление контекста пользователя
    const refresh = () => this.user.set(this.userContextSvc.currentUser);
    this.events.subscribe('changeBusinessFunction', refresh);
    this.events.subscribe('changeOrgUnit', refresh);
    this.destroyRef.onDestroy(() => {
      this.events.unsubscribe('changeBusinessFunction', refresh);
      this.events.unsubscribe('changeOrgUnit', refresh);
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeAll());
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update((v) => !v);
  }

  toggleDropdown(name: NavDropdown): void {
    this.ctxOpen.set(false);
    if (name === 'manager') this.reportAccessRequested.set(true);
    this.openedDropdown.update((current) => (current === name ? null : name));
  }

  closeAll(): void {
    this.openedDropdown.set(null);
    this.mobileMenuOpen.set(false);
    this.ctxOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    const root = this.ctxRoot()?.nativeElement;
    if (this.ctxOpen() && root && !event.composedPath().includes(root)) {
      this.ctxOpen.set(false);
    }

    if (!this.openedDropdown()) return;
    const dropdown = (event.target as HTMLElement | null)?.closest('.nav-item.dropdown');
    if (!dropdown || !this.host.nativeElement.contains(dropdown)) {
      this.openedDropdown.set(null);
    }
  }

  toggleCtx(): void {
    this.openedDropdown.set(null);
    this.ctxOpen.update((v) => !v);
    if (!this.ctxSeen()) {
      this.ctxSeen.set(true);
      try {
        localStorage.setItem('ctx.seen', '1');
      } catch {}
    }
  }

  closeCtx(): void {
    this.ctxOpen.set(false);
  }

  private readCtxSeen(): boolean {
    try {
      return localStorage.getItem('ctx.seen') === '1';
    } catch {
      return false;
    }
  }
}
